import type { Placement } from './types';

/**
 * Statuses excluded from the live book. Terminated (serving notice) is
 * deliberately NOT excluded — a contractor serving notice stays in the book
 * until their end date; the date checks remove them, not the status.
 * Comparisons are case-insensitive: Dataverse formats these as e.g.
 * "Awaiting start" while the mock data uses "Awaiting Start".
 */
const EXCLUDED_STATUS_PREFIXES = [
  'cancelled',
  'awaiting start',
  'starting soon',
];

const FUTURE_STARTER_STATUSES = new Set([
  'awaiting start',
  'awaiting approval',
  'awaiting paperwork',
  'paperwork sent',
]);

/** Effective end date for a placement — min(original, actual) if actual is set. */
export function effectiveEndDate(p: Placement): string {
  if (!p.actualEndDate) return p.originalEndDate;
  return p.actualEndDate < p.originalEndDate ? p.actualEndDate : p.originalEndDate;
}

/**
 * "Live Runner" definition from the rules doc:
 *   - has started (today >= start)
 *   - hasn't finished (today <= original end AND today <= actual end if set)
 *   - status does NOT start with Cancelled / Awaiting Start / Starting Soon
 *
 * This is also pinned in Claude's project memory.
 */
export function isRunner(p: Placement, asOf: string): boolean {
  if (p.startDate > asOf) return false;
  if (p.originalEndDate < asOf) return false;
  if (p.actualEndDate && p.actualEndDate < asOf) return false;
  const status = p.status.toLowerCase();
  if (EXCLUDED_STATUS_PREFIXES.some((pref) => status.startsWith(pref))) return false;
  return true;
}

/**
 * A placement in one of the "Awaiting …" / "Paperwork Sent" statuses that
 * has a start date in the future. Included in the forecast from its start.
 * Extensions never count — the contractor is already on site.
 */
export function isFutureStarter(p: Placement, asOf: string): boolean {
  if (p.extensionNumber > 0) return false;
  if (!FUTURE_STARTER_STATUSES.has(p.status.toLowerCase())) return false;
  return p.startDate > asOf;
}

/** Root placement code — the first segment of e.g. "004701/00/02". */
export function placementRoot(p: Placement): string {
  return p.idCode.split('/')[0];
}

/**
 * IDs of placements that already have a booked extension record in Mercury
 * (a placement sharing their root code with a higher extension number).
 * Their continuation is a real record, so they are neither at risk in the
 * renewal pool nor artificially extended — the successor record carries the
 * book forward from its own start date.
 */
export function bookedSuccessorIds(placements: Placement[]): Set<string> {
  const maxExtensionByRoot = new Map<string, number>();
  for (const p of placements) {
    const root = placementRoot(p);
    const prev = maxExtensionByRoot.get(root) ?? 0;
    if (p.extensionNumber > prev) maxExtensionByRoot.set(root, p.extensionNumber);
  }
  const out = new Set<string>();
  for (const p of placements) {
    if ((maxExtensionByRoot.get(placementRoot(p)) ?? 0) > p.extensionNumber) {
      out.add(p.id);
    }
  }
  return out;
}

/**
 * A placement counts toward the forecast (chart + projection) if it could
 * contribute to the book between today and 12 months out. This is a loose
 * filter: already-ended and cancelled placements are dropped, but anything
 * else — live, future starter, ending soon, serving notice — is kept.
 */
export function isForecastRelevant(p: Placement, asOf: string): boolean {
  const end = effectiveEndDate(p);
  if (end < asOf) return false;
  if (p.status.toLowerCase().startsWith('cancelled')) return false;
  return true;
}
